import { PingCacheDuration } from "@/constants/global";
import {
  AemRoleIds,
  AffiliationHeadRoleIds,
  AllAdminRoleIds,
  AllManagerRoleIds,
  AllStudentRoleIds,
  AllTeachingRoleIds,
} from "@/constants/userRoles";
import { LoginIdTypesEnum } from "@/enums/global";
import { StudentRoleIdsEnum } from "@/enums/userManagement";
import { gotoLogout, showCustomMessage } from "@/methods/utils";
import router from "@/routerInstance";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useOfflineDownloadStore } from "@/stores/core/offlineDownloadStore";
import { useServerClockStore } from "@/stores/core/serverClockStore";
import { useStudentStore } from "@/stores/student/studentStore";
import {
  ApiResponse,
  LoginWithAccessToken,
  LoginWithOtp,
  LoginWithPassword,
  RegisterB2CUser,
  SessionStore,
  UserAlert,
  UserInfo,
} from "@/types/global";
import { menuController } from "@ionic/vue";
import { HttpStatusCode } from "axios";
import { defineStore, storeToRefs } from "pinia";
import { useIonModalStore } from "./ionModalStore";

// Module-scoped, never store state: a Promise must not be made reactive or persisted. Every
// 403 that lands while a refresh is already in flight awaits this same call instead of firing
// its own. The staging cascade (2026-08-06) issued 58 refresh requests where one was needed —
// each failure independently triggering its own logout navigation.
let refreshTokenPromise: Promise<string | null> | null = null;

// Fail-safe for the one way the session-version check can go wrong. Its two halves are written by
// two different backend responses, so a deployment that serves AppVersionInfo.SessionVersion while
// the login response still omits it makes every freshly issued session fail the comparison — the
// user is signed out the instant they sign in, and no client release could clear it. Capping the
// forced sign-out at one per page load turns that into a single stray sign-out and then lets the
// (stale, but working) session through. A correctly configured deployment never reaches the cap:
// the session issued after the first sign-out already carries the current value. Module-scoped
// rather than store state so logging out cannot reset it — a reload is what re-arms it.
let hasForcedSessionVersionLogout = false;

function getInitialSessionStore(): SessionStore {
  const defaultUserInfo: UserInfo = {
    UserId: 0,
    UserName: "",
    EmailId: "",
    Mobile: "",
    RoleId: StudentRoleIdsEnum.STUDENT,
    AffiliationId: 0,
    AffiliationTypeId: 0,
    IsMultiAffiliation: false,
    IsDemoUser: false,
    UserDashboard: "",
    SessionUuid: "",
    AccessToken: null,
    RefreshToken: null,
    LoginSessionId: 0,
    CommunicationEmail: null,
    CommunicationMobile: null,
    ShowPasswordWarning: false,
    MustChangePassword: false,
    SessionVersion: null,
  };
  const initialSessionStore: SessionStore = {
    UserInfo: defaultUserInfo,
    UserAlerts: null,
    LastPingStatusCode: null,
    LastPingTimestamp: null,
    LastAccessedTime: null,
    DuplicateSessionInterval: null,
    LastDuplicateSessionChecked: null,
    ActiveCustomToasts: [],
  };

  return JSON.parse(JSON.stringify(initialSessionStore));
}

export const useSessionStore = defineStore("sessionStore", {
  state: (): SessionStore => getInitialSessionStore(),
  getters: {
    IsLoggedIn(): boolean {
      return this.UserInfo.AccessToken !== null;
    },
    IsStudent(): boolean {
      return Object.values(AllStudentRoleIds).includes(this.UserInfo.RoleId);
    },
    IsMustChangePasswordActive(): boolean {
      // BR-UI-101/102: non-student users with MustChangePassword are forced to /change-password;
      // student roles (11–18, incl. B2C and Basic Student) are exempt from the gate.
      return this.UserInfo.MustChangePassword && !this.IsStudent;
    },
    IsManagerRole(): boolean {
      return AllManagerRoleIds.includes(this.UserInfo.RoleId);
    },
    IsAdminRole(): boolean {
      return AllAdminRoleIds.includes(this.UserInfo.RoleId);
    },
    IsAemRole(): boolean {
      return AemRoleIds.includes(this.UserInfo.RoleId);
    },
    IsAffiliationHead(): boolean {
      return AffiliationHeadRoleIds.includes(this.UserInfo.RoleId);
    },
    IsTeacherRole(): boolean {
      return AllTeachingRoleIds.includes(this.UserInfo.RoleId);
    },
    // Server-controlled kill switch for existing sessions. Persisted state outlives an app update
    // on every platform we ship — a browser keeps localStorage across a deployment, and a Capacitor
    // WebView keeps it across a Play Store / App Store install and across a live-update bundle swap
    // — so a session captured by an older build is hydrated verbatim by the new one. UserInfo is
    // only ever written at login, which makes fields like RoleId silently stale after a release
    // that remaps them: the router's allowedRoleIds check then rejects the user permanently
    // (/unauthorized, with no path out) or, worse, matches a different role than the one the server
    // issued. Bumping AppVersionInfo.SessionVersion in Mongo discards every such session, with no
    // client release required.
    //
    // Both halves of the comparison are server-authored: the login response stamps the session
    // with the value that was current when it was issued, and latest-app-version-info reports the
    // value that is current now. The client never derives either one, so there is no window in
    // which it can stamp a session with a value the backend had already superseded.
    IsSessionVersionValid(): boolean {
      const { LatestAppVersion } = useAppSettingsStore();
      // Fail open. A missing server value means the document predates the field, not that every
      // session is invalid — treating it as a mismatch would sign out every user of any
      // environment whose AppVersionInfo has not been updated.
      if (!LatestAppVersion?.SessionVersion) {
        return true;
      }
      return this.UserInfo.SessionVersion === LatestAppVersion.SessionVersion;
    },
  },
  actions: {
    async beforeNavigateStart() {
      const serverClockStore = useServerClockStore();
      const { pingServerAndInitializeClock, getServerTimestamp } = serverClockStore;
      const latestTimestamp = await getServerTimestamp();
      // A zero timestamp means the clock could not be fetched, i.e. the server is unreachable.
      // Without this guard the subtraction below goes negative, reads as "inside the cache
      // window", and a persisted OK from the last session waves the navigation through as if
      // the server were healthy — the one case where the ping most needs to actually run.
      const isCacheValid =
        latestTimestamp > 0 &&
        this.LastPingTimestamp !== null &&
        this.LastPingStatusCode === HttpStatusCode.Ok &&
        latestTimestamp - this.LastPingTimestamp < PingCacheDuration;
      if (isCacheValid) {
        return;
      }
      this.LastPingStatusCode = await pingServerAndInitializeClock();
      this.LastPingTimestamp = latestTimestamp;
    },
    async afterNavigateValidated() {
      await useIonModalStore().dismissModal();
      await this.closeIonMenus();
      await useAppSettingsStore().deactivateAppFullscreen();
      await useAppSettingsStore().setAppScreenOrientation();
      await Promise.all(this.ActiveCustomToasts.map((toast) => toast?.dismiss?.()).filter(Boolean));
      this.ActiveCustomToasts = [];
      this.hideAppLoader();
    },
    async validateLoggedInSession() {
      // Checked first, for two reasons: no authenticated call should be made on behalf of a session
      // we have already decided to discard, and IsStudent is read further down from the very RoleId
      // a SessionVersion bump exists to invalidate. Safe to read LatestAppVersion here — the router
      // guard awaits initializeAppConfig() before this action, and a failure there routes to
      // UnderMaintenance instead of reaching us.
      if (this.IsLoggedIn && !this.IsSessionVersionValid && !hasForcedSessionVersionLogout) {
        hasForcedSessionVersionLogout = true;
        // isSticky keeps afterNavigateValidated from dismissing it on the way to LandingPage;
        // without a message an unprompted sign-out reads as the app losing the session by itself.
        showCustomMessage({
          messageText: "Please sign in again to continue on this updated version.",
          messageType: "warning",
          duration: 15,
          isSticky: true,
        });
        // Routed through the normal teardown so the server-side session is expired and offline
        // downloads are removed — dropping the persisted state directly would orphan both.
        await gotoLogout(true);
        return false;
      }
      const isDuplicateSession = await this.checkDuplicateLoginSession();
      if (isDuplicateSession) {
        return false;
      }
      if (!this.IsLoggedIn) {
        return true;
      }
      if (this.IsStudent) {
        const studentStore = useStudentStore();
        await studentStore.getEnrolledCourses(true);
      }
      this.ActiveCustomToasts = [];
      const appSettingsStore = useAppSettingsStore();
      const { IsCustomDomain, CustomAssets } = storeToRefs(appSettingsStore);
      const isValidUserInfo = this.UserInfo.UserId !== 0;
      const isValidCustomDomain =
        !IsCustomDomain.value ||
        (CustomAssets.value !== null &&
          CustomAssets.value.CustomDomain === window.location.hostname);
      if (!(isValidCustomDomain && isValidUserInfo)) {
        await gotoLogout(true);
        return false;
      }
      if (
        appSettingsStore.AppMenu !== null &&
        appSettingsStore.AppMenu.RoleId !== this.UserInfo.RoleId
      ) {
        appSettingsStore.resetMenus();
      }
      await appSettingsStore.getNavbarMenu();
      await this.initDuplicateSessionChecker();
      // Left un-awaited on purpose: alerts only feed a banner, and blocking the first paint on
      // them would add a round trip to every cold boot. Its 403 landing after the guard has
      // moved on is no longer a hazard — gotoLogout is idempotent and clears the credentials
      // itself, so a late arrival either logs out a genuinely dead session or does nothing.
      this.getUserAlerts();
      return true;
    },
    async initLoginWithPassword(loginCredentials: LoginWithPassword) {
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.loginWithPassword(loginCredentials);
      await this.processLoginResponse(response);
    },
    async initLoginWithOtp(loginCredentials: LoginWithOtp) {
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.loginWithOtp(loginCredentials);
      await this.processLoginResponse(response);
    },
    async initLoginWithThirdPartyAccessToken(loginCredentials: LoginWithAccessToken) {
      const appSettingsStore = useAppSettingsStore();
      const { ApiBaseUrl } = storeToRefs(appSettingsStore);
      const { default: UserApi } = await import("@/services/userApi");
      const userApi = new UserApi(ApiBaseUrl.value, loginCredentials.accessToken);
      const response = await userApi.loginWithThirdPartyAccessToken(loginCredentials);
      await this.processLoginResponse(response, loginCredentials.redirectTo);
    },
    async processLoginResponse(response: ApiResponse, redirectTo: string | null = null) {
      if (response.isSuccess) {
        const appSettingsStore = useAppSettingsStore();
        // UserInfo.SessionVersion rides in on the login response — the backend stamps the session
        // at the moment it issues it, so there is nothing to do here. See IsSessionVersionValid.
        this.UserInfo = response.data;
        // BR-UI-101 (usermgmt-8-2 hotfix): a gated user must reach /change-password WITHOUT any
        // other authenticated call — the backend 403s everything while MustChangePassword is
        // pending, and getNavbarMenu treats a non-404 failure as fatal (gotoLogout). The navbar
        // bootstrap is deferred to the post-change success path (UpdatePassword.onValidSubmit).
        if (this.IsMustChangePasswordActive) {
          router.push({ name: "ChangePassword" });
          return;
        }
        if (this.IsStudent) {
          const studentStore = useStudentStore();
          await studentStore.getEnrolledCourses(true);
        }
        appSettingsStore.resetMenus();
        await appSettingsStore.getNavbarMenu();
        const { AppMenu } = storeToRefs(appSettingsStore);
        if (AppMenu.value !== null) {
          redirectTo = redirectTo === null ? AppMenu.value.DefaultView : redirectTo;
          router.push({ name: redirectTo });
        }
      } else {
        showCustomMessage({
          messageText: response.errorMessage,
          messageType: "danger",
          duration: 60,
        });
      }
    },
    setUserCommunicationInfo(communicationType: string, communicationValue: string) {
      if (communicationType === LoginIdTypesEnum.EMAIL) {
        this.UserInfo.CommunicationEmail = communicationValue;
      } else if (communicationType === LoginIdTypesEnum.MOBILE) {
        this.UserInfo.CommunicationMobile = communicationValue;
      }
    },
    clearMustChangePassword() {
      // BR-UI-405: on a successful change-password in forced mode, clear the flag locally so
      // IsMustChangePasswordActive releases the gate. Called by ChangePassword.vue (usermgmt-8-2).
      this.UserInfo.MustChangePassword = false;
    },
    async submitB2CRegistration(registerCredentials: RegisterB2CUser) {
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.registerB2CUser(registerCredentials);
      if (response.isSuccess) {
        return { isSuccess: true, message: "Congrats, you have registered successfully." };
      } else {
        return { isSuccess: false, message: response.errorMessage };
      }
    },
    async getUserAlerts() {
      const apiServicesStore = useApiServicesStore();
      const response = await apiServicesStore.appSettingsApi.getUserAlerts();
      this.UserAlerts = response.isSuccess ? (response.data as Array<UserAlert>) : null;
    },
    async sendOtpForLogin(loginId: string | undefined) {
      if (!loginId) {
        showCustomMessage({
          messageText: "Please enter a valid Login Id to login with OTP!",
          messageType: "danger",
        });
        return;
      }
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const appSettingsStore = useAppSettingsStore();
      const response = await unverifiedUserApi.value.sendLoginOtp(
        loginId,
        appSettingsStore.AppName,
      );
      if (!(response.isSuccess && response.data)) {
        showCustomMessage({
          messageText: "There was an error sending an OTP to your Login Id!",
          messageType: "danger",
        });
        return;
      }
      return response.data;
    },
    async updateSessionLastAccessed() {
      const isMinTimeElapsed = await this.checkMinimumTimeElapsed(
        this.LastAccessedTime,
        import.meta.env.VITE_LAST_ACCESSED_GAP,
      );
      if (!isMinTimeElapsed) {
        return;
      }
      const apiServicesStore = useApiServicesStore();
      const { userApi } = storeToRefs(apiServicesStore);
      const serverClockStore = useServerClockStore();
      const { getServerTimestamp } = serverClockStore;
      await userApi.value.updateSessionLastAccessed(this.UserInfo.LoginSessionId);
      this.LastAccessedTime = await getServerTimestamp();
    },
    async initDuplicateSessionChecker() {
      if (this.DuplicateSessionInterval !== null) {
        clearInterval(this.DuplicateSessionInterval);
      }
      this.DuplicateSessionInterval = setInterval(
        async () => {
          await this.checkDuplicateLoginSession();
        },
        import.meta.env.VITE_DUPLICATE_LOGIN_CHECK_GAP,
      );
    },
    async checkDuplicateLoginSession() {
      if (!(useAppSettingsStore().IsProductionEnv && this.IsLoggedIn)) {
        return false;
      }
      const isMinTimeElapsed = await this.checkMinimumTimeElapsed(
        this.LastDuplicateSessionChecked,
        import.meta.env.VITE_DUPLICATE_LOGIN_CHECK_GAP,
      );
      if (!isMinTimeElapsed) {
        return false;
      }
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.checkDuplicateLoginSession(
        this.UserInfo.UserId,
        this.UserInfo.LoginSessionId,
      );
      if (response.isSuccess) {
        showCustomMessage({
          messageText: response.data,
          messageType: "danger",
          duration: 60,
          isSticky: true,
        });
        await gotoLogout(true);
        return true;
      }
      const serverClockStore = useServerClockStore();
      const { getServerTimestamp } = serverClockStore;
      this.LastDuplicateSessionChecked = await getServerTimestamp();
      return false;
    },
    // Returns a usable access token, or null when the session cannot be recovered. Callers MUST
    // treat null as terminal and stop — never fall back to the token already in the store.
    async refreshAccessToken(): Promise<string | null> {
      if (refreshTokenPromise !== null) {
        return refreshTokenPromise;
      }
      refreshTokenPromise = this.performTokenRefresh();
      try {
        return await refreshTokenPromise;
      } finally {
        refreshTokenPromise = null;
      }
    },
    async performTokenRefresh(): Promise<string | null> {
      if (this.UserInfo.RefreshToken === null) {
        return null;
      }
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.refreshAccessToken(this.UserInfo.RefreshToken);
      // An empty token on a 200 counts as a failure. Storing it would leave IsLoggedIn true
      // (AccessToken is no longer null) behind a credential that can never authenticate — a
      // session with no way out, since nothing would ever trigger the logout.
      const refreshedAccessToken = response.isSuccess ? (response.data as string) : null;
      if (!refreshedAccessToken) {
        // The refresh token is dead too, so nothing can revive this session. gotoLogout drops
        // the credentials before it routes, so by the time this returns IsLoggedIn is already
        // false and no further authenticated call can be started by anything still in flight.
        await gotoLogout(true);
        return null;
      }
      this.UserInfo.AccessToken = refreshedAccessToken;
      return refreshedAccessToken;
    },
    // Drops just the credentials, keeping the identity fields (UserId / LoginSessionId) that
    // the logout teardown still needs for expireLoginSession. Separate from resetStore() so it
    // can run synchronously, before any navigation, at the moment the session is known dead.
    clearCredentials() {
      this.UserInfo.AccessToken = null;
      this.UserInfo.RefreshToken = null;
    },
    async closeIonMenus() {
      const allMenus = await menuController.getMenus();
      if (allMenus.length > 0) {
        for (const menu of allMenus) {
          await menu.close(false);
        }
      }
    },
    hideAppLoader() {
      const loader = document.getElementById("app-loader");
      if (loader) {
        loader.style.display = "none";
      }
    },
    async checkMinimumTimeElapsed(
      lastCheckedTime: number | null | undefined,
      minimumGap: number,
    ): Promise<boolean> {
      if (lastCheckedTime === null || lastCheckedTime === undefined) {
        return true;
      }
      const serverClockStore = useServerClockStore();
      const { getServerTimestamp } = serverClockStore;
      const serverTimestamp = await getServerTimestamp();
      return serverTimestamp - lastCheckedTime >= minimumGap;
    },
    async resetStore() {
      if (this.DuplicateSessionInterval) {
        clearInterval(this.DuplicateSessionInterval);
      }
      try {
        const offlineDownloadStore = useOfflineDownloadStore();
        await offlineDownloadStore.removeAllDownloads();
      } catch (error) {
        console.warn("Error clearing offline downloads:", error);
      }
      this.$reset();
    },
  },
  persist: true,
});
